import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import Scroll from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faTasks, faTachometer, faEye, faPencil, faArrowLeft, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

import { EQUIPMENT_OPTIONS, USER_ACTIVITY_FEELING_WITH_EMPTY_OPTIONS, BREATHWORK_TYPE_WITH_EMPTY_OPTIONS } from "../../../Constants/FieldOptions";
import { putSessionUpdateAPI } from "../../../API/Session";
import { getSessionDetailAPI } from "../../../API/Session";
import FormErrorBox from "../../Reusable/FormErrorBox";
import FormInputField from "../../Reusable/FormInputField";
import FormTextareaField from "../../Reusable/FormTextareaField";
import FormMultiSelectField from "../../Reusable/FormMultiSelectField";
import FormRadioField from "../../Reusable/FormRadioField";
import FormDateTimeField from "../../Reusable/FormDateTimeField";
import FormCheckboxField from "../../Reusable/FormCheckboxField";
import FormSelectField from "../../Reusable/FormSelectField";
import PageLoadingContent from "../../Reusable/PageLoadingContent";
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom'
import { topAlertMessageState, topAlertStatusState, userDetailState } from "../../../AppState";


function UserSessionUpdate() {
    ////
    //// URL Parameters.
    ////

    const { uid, sid } = useParams()

    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);
    const [user, setUser] = useRecoilState(userDetailState);

    ////
    //// Component states.
    ////

    const [errors, setErrors] = useState({});
    const [isFetching, setFetching] = useState(false);
    const [forceURL, setForceURL] = useState("");
    const [created, setCreated] = useState();
    const [no, setNo] = useState();
    const [completedTime, setCompletedTime] = useState();
    const [duration, setDuration] = useState();
    const [totalDuration, setTotalDuration] = useState();
    const [name, setName] = useState();
    const [description, setDescription] = useState();
    const [brief, setBrief] = useState();
    const [equipment, setEquipment] = useState([]);
    const [isPrimarySession, setIsPrimarySession] = useState(false);
    const [targetHeartRate, setTargetHeartRate] = useState();
    const [targetHeartRateRatio, setTargetHeartRateRatio] = useState();
    const [sessionType, setSessionType] = useState();
    const [motivationalText, setMotivationalText] = useState();
    const [videoSections, setVideoSections] = useState([]);
    const [feeling, setFeeling] = useState();
    const [breathworkType, setBreathworkType] = useState("");

    ////
    //// Event handling.
    ////

    const onEquipmentChange = (e) => {
        console.log("onEquipmentChange, e:",e);
        let values = [];
        for (let option of e) {
            console.log("option:",option);
            values.push(option.value);
        }
        console.log("onEquipmentChange, values:",values);
        setEquipment(values);
    }

    const onSubmitClick = (e) => {
        console.log("onSubmitClick: Beginning...");
        const session = {
            id: sid,
            created: parseInt(created),
            userID: uid,
            no: parseInt(no),
            completedTime: completedTime,
            duration: duration,
            totalDuration: totalDuration,
            name: name,
            description: description,
            brief: brief,
            equipment: equipment,
            isPrimarySession: isPrimarySession,
            targetHeartRate: targetHeartRate,
            targetHeartRateRatio: targetHeartRateRatio,
            sessionType: sessionType,
            motivationalText: motivationalText,
            videoSections: videoSections,
            feeling: feeling,
            breathworkType: breathworkType,
        };
        console.log("onSubmitClick, session:", session);
        putSessionUpdateAPI(session, onSessionUpdateSuccess, onSessionUpdateError, onSessionUpdateDone);
    }

    ////
    //// API.
    ////

    function onSessionDetailSuccess(response){
        console.log("onSessionDetailSuccess: Starting...");
        setCreated(response.created);
        setNo(response.no);
        setCompletedTime(response.completedTime);
        setDuration(response.duration);
        setTotalDuration(response.totalDuration);
        setName(response.name);
        setDescription(response.description);
        setBrief(response.brief);
        setEquipment(response.equipment);
        setIsPrimarySession(response.isPrimarySession);
        setTargetHeartRate(response.targetHeartRate);
        setTargetHeartRateRatio(response.targetHeartRateRatio);
        setSessionType(response.sessionType);
        setMotivationalText(response.motivationalText);
        setVideoSections(response.videoSections);
        setFeeling(response.feeling);
        setBreathworkType(response.breathworkType);
    }

    function onSessionDetailError(apiErr) {
        console.log("onSessionDetailError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionDetailDone() {
        console.log("onSessionDetailDone: Starting...");
        setFetching(false);
    }

    function onSessionUpdateSuccess(response){
        // For debugging purposes only.
        console.log("onSessionUpdateSuccess: Starting...");
        console.log(response);

        // Add a temporary banner message in the app and then clear itself after 2 seconds.
        setTopAlertMessage("Session updated");
        setTopAlertStatus("success");
        setTimeout(() => {
            console.log("onSessionUpdateSuccess: Delayed for 2 seconds.");
            console.log("onSessionUpdateSuccess: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Redirect the user to a new page.
        setForceURL("/user/" + response.userId + "/session/" + sid);
    }

    function onSessionUpdateError(apiErr) {
        console.log("onSessionUpdateError: Starting...");
        setErrors(apiErr);

        // Add a temporary banner message in the app and then clear itself after 2 seconds.
        setTopAlertMessage("Failed submitting");
        setTopAlertStatus("danger");
        setTimeout(() => {
            console.log("onSessionUpdateError: Delayed for 2 seconds.");
            console.log("onSessionUpdateError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionUpdateDone() {
        console.log("onSessionUpdateDone: Starting...");
        setFetching(false);
    }

    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.
            setFetching(true);
            getSessionDetailAPI(
                sid,
                onSessionDetailSuccess,
                onSessionDetailError,
                onSessionDetailDone
            );
        }

        return () => { mounted = false; }
    }, [sid]);

    ////
    //// Component rendering.
    ////

    if (forceURL !== "") {
        return <Navigate to={forceURL}  />
    }

    return (
        <>
            <div class="container">
                <section class="section">
                    {/* Desktop Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class=""><Link to="/users" aria-current="page"><FontAwesomeIcon className="fas" icon={faUsers} />&nbsp;Users</Link></li>
                            <li class=""><Link to={`/user/${uid}/sessions`} aria-current="page"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Detail (Sessions)</Link></li>
                            <li class=""><Link to={`/user/${uid}/session/${sid}`} aria-current="page"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session</Link></li>
                            <li class="is-active"><Link to={`/user/${uid}/session/${sid}/edit`} aria-current="page"><FontAwesomeIcon className="fas" icon={faPencil} />&nbsp;Edit</Link></li>
                        </ul>
                    </nav>

                    {/* Mobile Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
                        <ul>
                            <li class=""><Link to={`/user/${uid}/session/${sid}`} aria-current="page"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Session</Link></li>
                        </ul>
                    </nav>

                    {/* Page content */}
                    <nav class="box">
                        <p class="title is-4"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session - {user.firstName} {user.lastName}</p>
                        <FormErrorBox errors={errors} />

                        <p class="subtitle is-6">Details</p>

                        {isFetching
                            ?
                            <PageLoadingContent displayMessage={"Please wait"} />
                            :
                            <>
                                <div class="container">

                                    <FormInputField
                                        label="No"
                                        name="no"
                                        type="number"
                                        placeholder="#"
                                        value={no}
                                        errorText={errors && errors.no}
                                        helpText=""
                                        onChange={(e)=>setNo(parseInt(e.target.value))}
                                        isRequired={true}
                                        maxWidth="75px"
                                    />

                                    <FormDateTimeField
                                        label="Completed Time (Optional)"
                                        name="completedTime"
                                        placeholder="Date input"
                                        value={completedTime}
                                        errorText={errors && errors.completedTime}
                                        helpText="The date/time this session was completed on"
                                        onChange={(date)=>setCompletedTime(date)}
                                        isRequired={true}
                                        maxWidth="350px"
                                    />

                                    <FormInputField
                                        label="Duration (Optional)"
                                        name="duration"
                                        type="number"
                                        placeholder="#"
                                        value={duration}
                                        errorText={errors && errors.duration}
                                        helpText="MEASURED IN MINUTES"
                                        onChange={(e)=>setDuration(parseInt(e.target.value))}
                                        isRequired={true}
                                        maxWidth="100px"
                                    />

                                    <FormInputField
                                        label="Total Duration (Optional)"
                                        name="totalDuration"
                                        type="number"
                                        placeholder="#"
                                        value={totalDuration}
                                        errorText={errors && errors.totalDuration}
                                        helpText="MEASURED IN MINUTES"
                                        onChange={(e)=>setTotalDuration(parseInt(e.target.value))}
                                        isRequired={true}
                                        maxWidth="100px"
                                    />

                                    <FormInputField
                                        label="Name"
                                        name="name"
                                        placeholder="Text input"
                                        value={name}
                                        errorText={errors && errors.name}
                                        helpText=""
                                        onChange={(e)=>setName(e.target.value)}
                                        isRequired={true}
                                        maxWidth="350px"
                                    />

                                    <FormTextareaField
                                        label="Description (Optional)"
                                        name="description"
                                        placeholder="Text input"
                                        value={description}
                                        errorText={errors && errors.description}
                                        helpText=""
                                        onChange={(e)=>setDescription(e.target.value)}
                                        isRequired={true}
                                        maxWidth="100%"
                                        rows={4}
                                    />

                                    <FormTextareaField
                                        label="Brief (Optional)"
                                        name="brief"
                                        placeholder="Text input"
                                        value={brief}
                                        errorText={errors && errors.brief}
                                        helpText=""
                                        onChange={(e)=>setBrief(e.target.value)}
                                        isRequired={true}
                                        maxWidth="100%"
                                        rows={2}
                                    />

                                    <FormTextareaField
                                        label="Motivational Text (Optional)"
                                        name="motivationalText"
                                        placeholder="Text input"
                                        value={motivationalText}
                                        errorText={errors && errors.motivationalText}
                                        helpText=""
                                        onChange={(e)=>setMotivationalText(e.target.value)}
                                        isRequired={true}
                                        maxWidth="100%"
                                        rows={2}
                                    />

                                    <FormMultiSelectField
                                        label="Equipment (Optional)"
                                        name="equipment"
                                        placeholder="Text input"
                                        options={EQUIPMENT_OPTIONS}
                                        selectedValues={equipment}
                                        onChange={onEquipmentChange}
                                        errorText={errors && errors.equipment}
                                        helpText="Pick the equipment associated with this session template."
                                        isRequired={true}
                                        maxWidth="320px"
                                    />

                                    <FormInputField
                                        label="Target Heart Rate (Optional)"
                                        name="targetHeartRate"
                                        type="number"
                                        placeholder="Number input"
                                        value={targetHeartRate}
                                        errorText={errors && errors.targetHeartRate}
                                        helpText=""
                                        onChange={(e)=>setTargetHeartRate(parseInt(e.target.value))}
                                        isRequired={true}
                                        maxWidth="95px"
                                    />

                                    <FormInputField
                                        label="Target Heart Rate Ratio (Optional)"
                                        name="targetHeartRateRatio"
                                        type="number"
                                        placeholder="Number input"
                                        value={targetHeartRateRatio}
                                        errorText={errors && errors.targetHeartRateRatio}
                                        helpText=""
                                        onChange={(e)=>setTargetHeartRateRatio(parseInt(e.target.value))}
                                        isRequired={true}
                                        maxWidth="75px"
                                    />

                                    <FormRadioField
                                        label="Session Type"
                                        name="sessionType"
                                        value={sessionType}
                                        opt1Value="base"
                                        opt1Label="Base"
                                        opt2Value="breath"
                                        opt2Label="Breath"
                                        opt3Value="sleep"
                                        opt3Label="Sleep"
                                        errorText={errors && errors.sessionType}
                                        onChange={(e)=>setSessionType(e.target.value)}
                                        maxWidth="180px"
                                    />

                                    <FormCheckboxField
                                        label="Is primary session?"
                                        name="isPrimarySession"
                                        checked={isPrimarySession}
                                        errorText={errors && errors.isPrimarySession}
                                        wasValidated={false}
                                        helpText=""
                                        onChange={(e)=>setIsPrimarySession(!isPrimarySession)}
                                    />

                                    <FormSelectField
                                        label="Feeling"
                                        name="feeling"
                                        placeholder="Pick"
                                        selectedValue={feeling}
                                        errorText={errors && errors.feeling}
                                        helpText=""
                                        onChange={(e)=>setFeeling(e.target.value)}
                                        options={USER_ACTIVITY_FEELING_WITH_EMPTY_OPTIONS}
                                    />

                                    <FormSelectField
                                        label="Breathwork Type (Optional)"
                                        name="breathworkType"
                                        placeholder="Pick"
                                        selectedValue={breathworkType}
                                        errorText={errors && errors.breathworkType}
                                        helpText=""
                                        onChange={(e)=>setBreathworkType(e.target.value)}
                                        options={BREATHWORK_TYPE_WITH_EMPTY_OPTIONS}
                                    />

                                    <div class="columns">
                                        <div class="column is-half">
                                            <Link to={`/user/${uid}/session/${sid}`} class="button is-hidden-touch"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back</Link>
                                            <Link to={`/user/${uid}/session/${sid}`} class="button is-fullwidth is-hidden-desktop"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back</Link>
                                        </div>
                                        <div class="column is-half has-text-right">
                                            <button class="button is-primary is-hidden-touch" onClick={onSubmitClick}><FontAwesomeIcon className="fas" icon={faCheckCircle} />&nbsp;Save</button>
                                            <button class="button is-primary is-fullwidth is-hidden-desktop" onClick={onSubmitClick}><FontAwesomeIcon className="fas" icon={faCheckCircle} />&nbsp;Save</button>
                                        </div>
                                    </div>

                                </div>
                            </>
                        }
                    </nav>
                </section>
            </div>
        </>
    );
}

export default UserSessionUpdate;
